import { useQueryClient } from "@tanstack/react-query";
import { useParams } from "next/navigation";
import { useEffect, useRef } from "react";
import { logoutAction } from "../../ui/blocks/sidebar-nav/logout-action";
import { type CustomError, extractErrorPageProps } from "../error.utils";
import { reportErrorPage } from "../error-page-telemetry";
import { defaultLocale, type Locales } from "../locales";
import { logger } from "../logger";
import { clearStoredPlpViewMode } from "../plp-view-preference";
import { useLocalizedRouter } from "./use-localized-router";

type UseErrorHandlerProps = {
  error?: CustomError;
  errorTranslations?: {
    subHeading500?: string;
    messageLine1?: string;
    messageLine2?: string;
    button5xx?: string;
  };
};

type UseErrorHandlerReturn = {
  errorPageProps: {
    errorCode?: "500";
    title?: string;
    description?: string[];
    buttonText?: string;
    imageSrc?: string;
    imageAlt?: string;
  };
  onButtonClick?: () => void;
};

const DEFAULT_ERROR_PROPS = {
  "500": {
    title: "Internal server error",
    description: [
      "Something went wrong on our end.",
      "Please try again later.",
    ],
  },
};

export function useErrorHandler(
  props: UseErrorHandlerProps
): UseErrorHandlerReturn {
  const { error, errorTranslations } = props;
  const router = useLocalizedRouter();
  const queryClient = useQueryClient();
  const params = useParams();
  const locale = (params?.locale as Locales) || defaultLocale;
  const reported = useRef<{
    error: CustomError | undefined;
    path: string;
  } | null>(null);
  useEffect(() => {
    const path = window.location.pathname;
    // Repeated logging of the same error and path is suppressed
    // while the hook remains mounted.
    if (reported.current?.error === error && reported.current?.path === path) {
      return;
    }
    reported.current = { error, path };
    const message = "500 error page displayed";
    const reportedError = error ?? new Error(message);
    // Displaying the error page creates a log event, including the page path
    // and the error's message and digest, when available.
    void logger.error(message, reportedError, {
      event: "error_page_displayed",
      error_page: {
        code: 500,
        kind: error ? "client_exception" : "error_boundary_fallback",
        path,
        ...(error?.digest ? { digest: error.digest } : {}),
        ...(error?.message ? { message: error.message } : {}),
      },
    });
    reportErrorPage({
      digest: error?.digest,
      kind: error ? "client_exception" : "error_boundary_fallback",
      pathname: path,
    });
  }, [error]);

  const customErrorProps = extractErrorPageProps(error);

  const errorCode = "500";

  const title =
    customErrorProps.title ||
    errorTranslations?.subHeading500 ||
    DEFAULT_ERROR_PROPS[errorCode as keyof typeof DEFAULT_ERROR_PROPS]?.title;

  const description =
    customErrorProps.description ||
    (errorTranslations?.messageLine1 && errorTranslations?.messageLine2
      ? [errorTranslations.messageLine1, errorTranslations.messageLine2]
      : undefined) ||
    DEFAULT_ERROR_PROPS[errorCode as keyof typeof DEFAULT_ERROR_PROPS]
      ?.description;

  const buttonText =
    customErrorProps.buttonText || errorTranslations?.button5xx || undefined;

  return {
    errorPageProps: {
      errorCode,
      title,
      description,
      buttonText,
      imageSrc: "/assets/404-500-background.jpg",
      imageAlt: "Error page background",
    },
    onButtonClick: async () => {
      let redirectTo = `/${locale}/login`;
      try {
        const result = await logoutAction(locale);
        redirectTo = result.redirectTo;
      } catch (_error) {
        // The session may already have expired; still return to login.
      } finally {
        queryClient.clear();
        clearStoredPlpViewMode();
      }

      // Same-path client navigation leaves Next's failed boundary mounted.
      // Account for trailing-slash normalization when already on login.
      const pathname = window.location.pathname.replace(/\/$/, "");
      if (pathname === `/${locale}/login`) {
        window.location.assign(redirectTo);
        return;
      }
      router.push(redirectTo);
    },
  };
}
